import PaginatedResponse, { type PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import ProposalReviewerCandidateSchema, {
  type ProposalReviewerCandidateJSON,
} from "work/proposals/reviewers/_schemas/proposal-reviewer-candidate-schema";
import z from "zod";

export const REVIEWER_CANDIDATES_PAGE_SIZE = 25;

export type ProposalReviewerCandidatesArgs = {
  /** The proposal the popover is for, or undefined for a draft that has not been saved yet. */
  negotiationId?: string;
  search: string;
  /** Whether the leader is the vendor's. Only says anything for a draft that has not been saved yet. */
  asVendor?: boolean;
  /**
   * The vendor team as the page has it, unsaved edits included, each as `userId:legalEntityId`. The
   * server offers the ones it has not saved yet alongside the saved team (#5404). Sorted, so the same
   * team is the same cache entry.
   */
  teamMembers?: string[];
};

export type ProposalReviewerCandidatesPage = {
  data: ProposalReviewerCandidateJSON[];
  headers: PaginationHeaders;
};

export function getProposalReviewerCandidatesUrl(negotiationId?: string): string {
  return negotiationId
    ? `work/proposal-negotiations/${negotiationId}/reviewer-candidates`
    : "work/proposal-negotiations/reviewer-candidates";
}

/**
 * The query string, built by hand because each team member is its own `teamMember` parameter and
 * `fetchBaseQuery` would join an array into one.
 */
export function getProposalReviewerCandidatesQuery(args: ProposalReviewerCandidatesArgs, pageIndex: number): string {
  const params = new URLSearchParams();
  if (args.search) params.set("search", args.search);
  params.set("pageIndex", String(pageIndex));
  params.set("pageSize", String(REVIEWER_CANDIDATES_PAGE_SIZE));
  if (args.asVendor && !args.negotiationId) params.set("vendor", "true");
  args.teamMembers?.forEach((member) => params.append("teamMember", member));
  return params.toString();
}

/**
 * The page after `lastPage`, or undefined at the end. Read from the index and the page count rather
 * than the header's `hasNext`, which the API computes as `pageIndex < totalPages` and so reports one
 * page more than there is.
 */
export function getNextReviewerCandidatesPageIndex(lastPage: ProposalReviewerCandidatesPage): number | undefined {
  const nextPageIndex = lastPage.headers.pageIndex + 1;
  return nextPageIndex < lastPage.headers.totalPages ? nextPageIndex : undefined;
}

export const proposalReviewerCandidateApi = api.injectEndpoints({
  endpoints: (build) => ({
    // One cache entry per proposal and search; the pages of it are the page index. The search is
    // the server's, so a later page is never filtered out of an earlier one.
    getProposalReviewerCandidates: build.infiniteQuery<
      ProposalReviewerCandidatesPage,
      ProposalReviewerCandidatesArgs,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) => getNextReviewerCandidatesPageIndex(lastPage),
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${getProposalReviewerCandidatesUrl(queryArg.negotiationId)}?${getProposalReviewerCandidatesQuery(queryArg, pageParam)}`,
      }),
      rawResponseSchema: z.array(ProposalReviewerCandidateSchema.ProposalReviewerCandidateJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: ProposalReviewerCandidateJSON[], meta): ProposalReviewerCandidatesPage => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
    }),
  }),
});

export const { useGetProposalReviewerCandidatesInfiniteQuery } = proposalReviewerCandidateApi;
